// Which prior authorization was in force on a given day.
//
// A GAPP claim carries the PA number issued against the DMA-80. Until now
// claim lines were created with prior_auth_number blank — the GAMMIS CSV has a
// column for it and the 837P emits it, and both were sending nothing unless
// somebody typed the number into each line by hand. A claim filed without one
// comes back CO-15, "authorization number missing/invalid".
//
// The number is resolved per line rather than per run because a run spans
// dates and an authorization has a window. A service date in one month and a
// renewal in the next belong to different authorizations, and the line has to
// carry the one that was actually in force when the care happened — not
// whichever is current at the moment the run is built.
//
// `prior_auth_history` is the version log: committing a renewal supersedes the
// previous row and inserts a new active one, so superseded rows are exactly
// where an older service date finds its authorization. They are candidates
// here, not noise. `clients.dma_80_pa_number` is the current-value cache and
// is the fallback for clients whose history predates the log.
//
// Dates are compared as ISO strings. Every date here is a Postgres `date`
// rendered as YYYY-MM-DD and every service date is the first ten characters of
// an ISO timestamp, so lexicographic order is chronological order and no
// timezone gets a chance to move a day.

/** The auth_type on prior_auth_history that a home-health claim bills under. */
export const CLAIM_AUTH_TYPE = 'PA';

const day = (v) => (typeof v === 'string' && v.length >= 10 ? v.slice(0, 10) : null);

// The first day an authorization covers. `effective_date` is the DCH start
// date (added 2026-10-19). Older rows have only `submitted_date`, which on
// some of them is the day the packet went to DCH rather than the start — the
// ordering in authorizationInForce is what keeps those from claiming weeks
// their predecessor still held.
const startOf = (row) => day(row.effective_date) || day(row.submitted_date);

// An authorization covers a date when it had started by then and has not
// expired. A null expiry is open-ended: several live PA rows carry no expiry
// date, and treating that as "expired" would strip the number from every one
// of their claims.
const covers = (row, date) => {
  const from = startOf(row);
  const to   = day(row.expiry_date);
  if (from && from > date) return false;
  if (to && to < date) return false;
  return true;
};

/**
 * The authorization row in force on a date, or null. The one place that
 * decides it: claim numbers and the self-audit's authorised hours both ask
 * here.
 *
 * When more than one covers the date, the one that expires first wins. A
 * renewal takes over when its predecessor runs out, so where a renewal was
 * recorded with its submission date and appears to overlap, the predecessor
 * still holds the overlap (G005, G011, G047, G030 in July–August 2026). Two
 * rows ending the same day are a correction of one authorization: the later
 * start, then the later entry, is the one to bill.
 *
 * @param {any[]} history  prior_auth_history rows (any type, any status)
 * @param {string} date    YYYY-MM-DD
 * @param {{ requireNumber?: boolean }} [opts]
 * @returns {any}
 */
export const authorizationInForce = (history, date, { requireNumber = true } = {}) => {
  const on = day(date);
  if (!on) return null;
  const endKey = (r) => day(r.expiry_date) || '9999-12-31';
  return (history || [])
    .filter(r => r && r.auth_type === CLAIM_AUTH_TYPE && (!requireNumber || r.auth_number))
    .filter(r => covers(r, on))
    .sort((a, b) =>
      endKey(a).localeCompare(endKey(b))
      || String(startOf(b) || '').localeCompare(String(startOf(a) || ''))
      || String(b.created_at || '').localeCompare(String(a.created_at || '')))[0] || null;
};

/**
 * Resolve the authorization number for one claim line.
 *
 * history — prior_auth_history rows for this client (any type, any status)
 * client  — the clients row, for the cached current number
 * date    — the service date, YYYY-MM-DD
 *
 * Returns { number, source, expiresOn, reason }. `number` is null when nothing
 * can be resolved, and `reason` then says why, so a run can report the
 * difference between a client who has no authorization on file and one whose
 * authorization had lapsed by the date the care was given.
 *
 * The JSDoc types are load-bearing: edge functions import this module through
 * supabase/functions/_shared/lib and CI type-checks them under Deno, where an
 * unannotated `history = []` infers as never[] and rejects every caller.
 *
 * `allowLapsed` falls back to the most recently expired authorisation when
 * none is in force. It is OFF by default, because a run should record what was
 * actually authorised — and a lapsed number in `claim_lines.prior_auth_number`
 * would read as a current one to everything downstream. It is turned on at
 * FILING time only, where the choice is between a claim that denies
 * "authorisation not valid for these dates" and one that denies "no
 * authorisation on file". The first is more specific, more appealable, and
 * reprocesses cleanly if the renewal comes back dated to cover the gap.
 *
 * @param {{ history?: any[], client?: any, date?: string|null, allowLapsed?: boolean }} [args]
 * @returns {{ number: string|null, source: string|null, expiresOn: string|null, reason: string|null }}
 */
export const resolvePriorAuth = ({ history = [], client = null, date, allowLapsed = false } = {}) => {
  const on = day(date);
  if (!on) return { number: null, source: null, expiresOn: null, reason: 'no_service_date' };

  const candidates = (history || []).filter(
    r => r && r.auth_type === CLAIM_AUTH_TYPE && r.auth_number,
  );

  const inForce = authorizationInForce(candidates, on);

  if (inForce) {
    return {
      number: String(inForce.auth_number).trim(),
      source: 'history',
      expiresOn: day(inForce.expiry_date),
      reason: null,
    };
  }

  // No logged authorization covers the date. The client record still caches a
  // current number, which is all there is for clients whose authorizations
  // predate the history log — but only honour it when it was plausibly valid
  // on the day, otherwise this quietly reintroduces the wrong number.
  const cached = client?.dma_80_pa_number ? String(client.dma_80_pa_number).trim() : '';
  const cachedExpiry = day(client?.pa_expiry_date);
  if (cached && (!cachedExpiry || cachedExpiry >= on)) {
    return { number: cached, source: 'client', expiresOn: cachedExpiry, reason: null };
  }

  if (!candidates.length && !cached) {
    return { number: null, source: null, expiresOn: null, reason: 'none_on_file' };
  }
  // Something exists, but not for this day. Distinguish the two so the run can
  // say "lapsed before this service date" rather than "missing".
  const anyStartsLater = candidates.some(r => { const f = startOf(r); return f && f > on; });
  const reason = anyStartsLater ? 'not_yet_effective' : 'expired';

  // Filing with the lapsed number rather than none at all. Only ever a
  // *past* authorisation — one that begins later has never covered any care
  // and putting it on a claim would assert something untrue about the dates.
  if (allowLapsed && !anyStartsLater) {
    const lapsed = candidates
      .filter(r => { const t = day(r.expiry_date); return t && t < on; })
      .sort((a, b) => String(day(b.expiry_date) || '').localeCompare(String(day(a.expiry_date) || '')))[0];
    const fallback = lapsed
      ? { number: String(lapsed.auth_number).trim(), expiresOn: day(lapsed.expiry_date) }
      : (cached && cachedExpiry && cachedExpiry < on ? { number: cached, expiresOn: cachedExpiry } : null);
    if (fallback) {
      return { number: fallback.number, source: 'lapsed', expiresOn: fallback.expiresOn, reason };
    }
  }

  return { number: null, source: null, expiresOn: null, reason };
};

/**
 * The authorization number a filed claim carries — the CSV and the 837P both
 * ask here, so the two exports of one run can never disagree. Until
 * 2026-10-19 the CSV sent the stored number and the 837P filled blanks at
 * filing time, so a line with no stored number went out blank in one and
 * with a lapsed number in the other.
 *
 * The line's own number when it has one; otherwise what was in force on its
 * date, falling back to the lapsed one (see allowLapsed above for why that is
 * the better denial). The claim line itself is never rewritten.
 *
 * @param {{ line?: any, history?: any[], client?: any }} [args]
 * @returns {string}
 */
export const authNumberForFiling = ({ line = null, history = [], client = null } = {}) =>
  String(line?.prior_auth_number || '').trim()
  || resolvePriorAuth({ history, client, date: line?.service_date, allowLapsed: true }).number
  || '';

export const AUTH_REASON_LABEL = {
  none_on_file: 'no authorization on file',
  expired: 'authorization had lapsed by the service date',
  not_yet_effective: 'authorization begins after the service date',
  no_service_date: 'no service date on the visit',
};

/** Index history rows by client uuid, for resolving a run line by line. */
export const authHistoryByClient = (rows = []) => {
  const by = {};
  for (const r of rows || []) {
    if (!r?.client_id) continue;
    (by[r.client_id] = by[r.client_id] || []).push(r);
  }
  return by;
};
